import { useEffect, useRef, useState } from 'react';

import { DOC_PAGES, DOCS } from '../docs/content.tsx';
import { useApp } from '../state.tsx';

/** Protocol documentation: sections in a sidebar, one page at a time, its headings listed on the right */
export function DocsPage() {
  const { route } = useApp();
  const page = DOC_PAGES.find((p) => p.slug === route.doc) ?? DOC_PAGES[0]!;
  const i = DOC_PAGES.indexOf(page);
  const prev = DOC_PAGES[i - 1];
  const next = DOC_PAGES[i + 1];
  const article = useRef<HTMLElement>(null);
  const [toc, setToc] = useState<{ id: string; text: string }[]>([]);

  // headings come from the rendered page, so the outline never drifts from the content
  useEffect(() => {
    const hs = article.current?.querySelectorAll('h2[id]') ?? [];
    setToc([...hs].map((h) => ({ id: h.id, text: h.textContent ?? '' })));
    // on a phone the sidebar is a horizontal strip: bring the current page into view
    document.querySelector('.docs-nav a.is-on')?.scrollIntoView({ block: 'nearest', inline: 'center' });
    window.scrollTo(0, 0);
  }, [page]);

  return (
    <div className="docs">
      <nav className="docs-nav" aria-label="Documentation">
        {DOCS.map((s) => (
          <div key={s.label} className="docs-nav-section">
            <h4>{s.label}</h4>
            {s.pages.map((p) => (
              <a key={p.slug} href={`#/docs/${p.slug}`} className={p === page ? 'is-on' : ''} aria-current={p === page ? 'page' : undefined}>
                {p.title}
              </a>
            ))}
          </div>
        ))}
      </nav>

      <article className="docs-article" ref={article}>
        <div className="docs-eyebrow">{DOCS.find((s) => s.pages.includes(page))?.label}</div>
        <h1>{page.title}</h1>
        <p className="docs-lead">{page.lead}</p>
        {page.body}
        <footer className="docs-pager">
          {prev ? (
            <a href={`#/docs/${prev.slug}`}>
              <small>Previous</small>
              {prev.title}
            </a>
          ) : (
            <span />
          )}
          {next && (
            <a href={`#/docs/${next.slug}`} className="next">
              <small>Next</small>
              {next.title}
            </a>
          )}
        </footer>
      </article>

      <aside className="docs-toc">
        {toc.length > 0 && (
          <>
            <h4>On this page</h4>
            {toc.map((t) => (
              <button key={t.id} className="link" onClick={() => document.getElementById(t.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                {t.text}
              </button>
            ))}
          </>
        )}
      </aside>
    </div>
  );
}
